import { gentleMode } from './settings';

/**
 * Gentle Mode, as a table.
 *
 * Spec §2: *"an optional **Gentle Mode** toggle that lengthens cutaways and
 * mutes the worst audio."* This file is both halves of that sentence and is the
 * only place either is written down.
 *
 * ── Why a table and not `if (gentle)` in seven directors ────────────────────
 *
 * Because a mode scattered across seven files is seven places to keep true and a
 * mode nobody can audit. There is no way to answer *"what does Gentle Mode
 * actually do?"* by reading a codebase that answers it in fragments, and a
 * content mode that cannot be audited is worse than none: it makes a promise to
 * the player it has no way of checking it kept. `gentle-mode.spec.ts` asserts a
 * DELTA against this table (D-100) — one run with it off, one with it on,
 * everything else held still.
 *
 * ── The rule that decides what goes in the mute list ────────────────────────
 *
 * **Gentle Mode never mutes a sound the player needs.** This game's threats are
 * legible by ear on purpose — the dog is heard before it is seen, and spec §9's
 * fairness requirement (*"every detection is telegraphed and explainable"*) is
 * partly an audio requirement. A gentle mode that silences the dog does not
 * spare the player anything; it makes them lose without being told why, which is
 * cruelty of a different kind. So `dog-claws-concrete`, `dog-pant`,
 * `dog-snuff` and `handler-whistle` are **deliberately absent** from this list
 * and this paragraph is why.
 *
 * ── Why the list is short, and why that is not a stub ───────────────────────
 *
 * The audio direction got there first. `cull.ts`'s header states its own brief —
 * *"Beat 5 is not a scare scene... this set avoids calls, rises and tails that
 * would make the room appear to be responding to her"* — and `bin-lid-close` is
 * documented as deliberately having no *"dramatic tail or horror-film slam."*
 * There is not much worst audio to mute because it was never written. What is
 * left is the one sound whose *meaning* is the worst thing in the game so far.
 */

/**
 * Sounds Gentle Mode does not start at all.
 *
 * `plant-cycle` is the killing floor heard through plastic sheeting: a 4.2 s
 * thump-and-hiss that repeats for the several minutes beat 5's corridor takes.
 * Its texture is mild and its implication is not, and unlike the dog it warns of
 * nothing and gates nothing — the red lamp is beat 5's destination, and the
 * corridor still reads without this bed under it.
 *
 * `bin-lid-close` is NOT here, and that is the same call in the other direction:
 * it is one physical event, it is the last thing she hears from outside, and
 * removing it would not soften the beat, it would delete it.
 */
export const GENTLE_MUTED_SOUNDS: readonly string[] = ['plant-cycle'];

/**
 * How much longer a cutaway holds, in seconds, with Gentle Mode on.
 *
 * "Lengthens cutaways" means the player spends longer looking away from the
 * thing, not longer looking at it. In a game whose worst moment is authored as
 * an ABSENCE — beat 1 takes the littermate in the dark, with no animation, no
 * hand and no sound — the cutaway *is* the dark, and lengthening it means the
 * lamp comes back later.
 *
 * 1.8 s, against beat 1's roughly 2 s of shadow: near enough to double it
 * without turning a held breath into a wait. It is a constant rather than a
 * multiplier because the thing being extended is a human span — the time it
 * takes to look away and back — and that does not scale with the length of what
 * it is covering.
 */
export const GENTLE_CUTAWAY_EXTRA_SECONDS = 1.8;

/** The mute list when Gentle Mode is on, and nothing when it is off. */
export function gentleMutedSounds(): readonly string[] {
  return gentleMode() ? GENTLE_MUTED_SOUNDS : [];
}

/**
 * Extra seconds to hold a cutaway. **Zero unless Gentle Mode is on**, which is
 * what lets a director add `+ gentleCutawayExtraSeconds()` to a hold without
 * acquiring a branch or a second timing path.
 */
export function gentleCutawayExtraSeconds(): number {
  return gentleMode() ? GENTLE_CUTAWAY_EXTRA_SECONDS : 0;
}
